import { useMemo } from 'react';
import { usePredictionMarketCategories } from '@/lib/endpoints';
import { TRENDING_CATEGORY_CODE, useAppSearchParams } from '@/hooks/useAppSearchParams';
import { useHiddenCategories } from '@/lib/territory/useTerritory';
import type { CategoryResponseDto } from '@/lib/models';
import { CategoryTabs } from '../CategoryTabs/CategoryTabs';
import { CategoryTabsSkeleton } from '../CategoryTabs/CategoryTabsSkeleton';

/**
 * The Live page's category pills — the lobby container with one difference:
 * the synthetic first pill is labelled **All** (the page's default, spec
 * §4.2) instead of Trending. It keeps the TRENDING code as the "no filter"
 * sentinel so the `?category=` URL contract, `setCategory` deletion, and
 * {@link SubCategoryBar} behavior stay identical to the lobby.
 */
export function LiveCategoryTabsContainer({
  className,
  presentCategories,
}: {
  className?: string;
  /** Category codes with eligible events on the page — pills for empty categories are hidden. */
  presentCategories: ReadonlySet<string>;
}) {
  const { categoryCode: activeCategory, setCategory } = useAppSearchParams();

  const { data, isLoading } = usePredictionMarketCategories();

  // Categories blocked for the user's territory (country ∪ state) never
  // render; neither do categories with nothing live on the page.
  const hiddenCategories = useHiddenCategories();
  const visible = useMemo(
    () =>
      (data ?? []).filter((cat) => !hiddenCategories.includes(cat.code.toUpperCase()) && presentCategories.has(cat.code)),
    [data, hiddenCategories, presentCategories],
  );

  const categories: CategoryResponseDto[] = [
    { id: 'all', displayName: 'All', code: TRENDING_CATEGORY_CODE, subCategories: [] },
    ...visible,
  ];

  if (isLoading) return <CategoryTabsSkeleton className={className} />;
  if (!data || data.length === 0) return null;

  return (
    <div className="w-full">
      <CategoryTabs
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={setCategory}
        className={className}
      />
    </div>
  );
}
